"use client";

import React, { useRef } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Label } from "@/app/components/ui/label";
import { Button } from "@/app/components/ui/button";
import { Badge } from "@/app/components/ui/badge";
import { 
  Download as DownloadIcon,
  Eye,
  BarChart3,
  Copy,
  Check,
  Settings,
} from "lucide-react";
import DownloadComponent from "@/app/components/DownloadComponent";
import { EncodedQr } from "@/lib/qr/types";
import { QRState } from "./types";

interface QRPreviewProps {
  qrResult: EncodedQr | null;
  state: QRState;
  dynamicQr: any;
  copiedPayload: boolean;
  copiedImageUrl: boolean;
  copiedDynamicUrl: boolean;
  onCopyPayload: () => void;
  onCopyImageUrl: () => void;
  onCopyDynamicUrl: (url: string) => void;
  onDownload: (format: string, filename: string) => void;
}

export default function QRPreview({
  qrResult,
  state,
  dynamicQr,
  copiedPayload,
  copiedImageUrl,
  copiedDynamicUrl,
  onCopyPayload,
  onCopyImageUrl,
  onCopyDynamicUrl,
  onDownload,
}: QRPreviewProps) {
  const qrContainerRef = useRef<HTMLDivElement | null>(null);

  return (
    <Card className="card-enhanced">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Eye className="h-5 w-5" />
          QR Code Preview
        </CardTitle>
        <CardDescription>
          Your generated QR code and download options
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {qrResult ? (
          <>
            {/* QR Code Display */}
            <div className="flex justify-center">
              <div
                ref={qrContainerRef}
                className="p-4 rounded-lg border-2 border-dashed border-muted-foreground/25"
                style={{ backgroundColor: state.colorLight }}
              >
                <img
                  src={qrResult.dataURL}
                  alt="Generated QR Code"
                  className="max-w-full h-auto"
                  style={{ maxWidth: '300px' }}
                />
              </div>
            </div>

            {/* QR Code Info */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Format:</span>
                <Badge variant="secondary">
                  {qrResult.mime === 'image/svg+xml' ? 'SVG' : 'PNG'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Size:</span>
                <span className="text-sm text-muted-foreground">{qrResult.size}px</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Error Correction:</span>
                <Badge variant="outline">{state.ecl}</Badge>
              </div>
            </div>

            {/* Dynamic QR Info */}
            {dynamicQr && (
              <div className="space-y-3 p-4 border rounded-lg bg-blue-50 dark:bg-blue-950/20">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Dynamic QR Code
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span>Dynamic URL:</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onCopyDynamicUrl(dynamicQr.target.dynamicUrl)}
                      className="h-6 px-2"
                    >
                      {copiedDynamicUrl ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground break-all">
                    {dynamicQr.target.dynamicUrl}
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Target URL:</span>
                    <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {dynamicQr.target.url}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Scans:</span>
                    <Badge variant="outline">{dynamicQr.target.scanCount || 0}</Badge>
                  </div>
                </div>
              </div>
            )}

            {/* Payload Preview */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Encoded Payload:</Label>
              <div className="p-3 bg-muted rounded-lg">
                <code className="text-xs break-all">
                  {qrResult.payload}
                </code>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onCopyPayload}
                className="w-full"
              >
                {copiedPayload ? <Check className="mr-2 h-3 w-3" /> : <Copy className="mr-2 h-3 w-3" />}
                Copy Payload
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Settings className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No QR Code Generated</h3>
            <p className="text-muted-foreground text-sm">
              Fill in the form and click "Generate QR Code" to create your QR code
            </p>
          </div>
        )}
      </CardContent>
      
      {qrResult && (
        <CardFooter className="flex flex-col gap-3">
          <DownloadComponent
            data={qrResult.dataURL}
            filename="qrcode"
            onDownload={onDownload}
            className="w-full"
            allowedCategories={['image', 'document']}
          >
            <DownloadIcon className="mr-2 h-4 w-4" />
            Download QR Code
          </DownloadComponent>
          
          <Button
            variant="outline"
            onClick={onCopyImageUrl}
            className="w-full"
          >
            {copiedImageUrl ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            Copy Image URL
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
